import { useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useOfficers } from '../hooks/useOfficers';
import { ROLES } from '../../../shared/constant/roles';
import { confirmAction } from '../../../shared/utils/dialogs';

const roleOptions = [ROLES.OFFICER, ROLES.ADMIN, ROLES.SUPER_ADMIN];

function UsersPage() {
  const { role: myRole, user } = useAuth();
  const canManage = myRole === ROLES.SUPER_ADMIN;
  const { officers, loading, error, updateRole, updateStatus, remove } = useOfficers();
  const [busyId, setBusyId] = useState(null);

  const visibleOfficers =
    myRole === ROLES.OFFICER ? officers.filter((o) => o.role !== ROLES.SUPER_ADMIN) : officers;

  const withBusy = async (authId, action) => {
    setBusyId(authId);
    try {
      await action();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (authId, name) => {
    const confirmed = await confirmAction({
      title: 'Remove officer?',
      text: `Remove ${name} from the officers list? This cannot be undone.`,
      confirmText: 'Remove',
      danger: true,
    });
    if (!confirmed) return;
    withBusy(authId, () => remove(authId));
  };

  return (
    <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 bg-navy-light">
        <h2 className="text-lg font-semibold text-white">Users</h2>
        <p className="text-sm text-gray-200">Officers registered in the system</p>
      </div>

      {loading ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">Loading users…</p>
      ) : error ? (
        <p className="px-5 py-8 text-sm text-danger text-center">{error}</p>
      ) : visibleOfficers.length === 0 ? (
        <p className="px-5 py-8 text-sm text-gray-500 text-center">No users found</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-md text-gray-500 uppercase">
                <th className="py-2 pl-5 pr-4 font-semibold whitespace-nowrap">Name</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Email</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Role</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Status</th>
                <th className="py-2 pr-4 font-semibold whitespace-nowrap">Approved</th>
                {canManage && <th className="py-2 pr-5 font-semibold whitespace-nowrap">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visibleOfficers.map(({ auth_id, firstname, lastname, email, role, status, approved }) => {
                const isSelf = auth_id === user?.id;
                const isBusy = busyId === auth_id;
                return (
                  <tr key={auth_id} className="hover:bg-gold/10">
                    <td className="py-3 pl-5 pr-4 font-medium text-gray-900 whitespace-nowrap">
                      {firstname} {lastname} {isSelf && <span className="text-xs text-gray-400">(you)</span>}
                    </td>
                    <td className="py-3 pr-4 text-gray-700 whitespace-nowrap">{email}</td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {canManage ? (
                        <select
                          value={role}
                          disabled={isSelf || isBusy}
                          onChange={(e) => withBusy(auth_id, () => updateRole(auth_id, e.target.value))}
                          className="rounded-md border border-gray-300 text-sm py-1 px-2 disabled:opacity-50"
                        >
                          {roleOptions.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="capitalize">{role}</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {canManage ? (
                        <button
                          type="button"
                          disabled={isSelf || isBusy}
                          onClick={() =>
                            withBusy(auth_id, () =>
                              updateStatus(auth_id, status === 'Active' ? 'Inactive' : 'Active')
                            )
                          }
                          className={`px-2 py-1 rounded-full text-xs font-medium disabled:opacity-50 ${
                            status === 'Active' ? 'bg-success/10 text-success' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {status}
                        </button>
                      ) : (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            status === 'Active' ? 'bg-success/10 text-success' : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {status}
                        </span>
                      )}
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          approved ? 'bg-navy/10 text-navy' : 'bg-warning/10 text-warning-dark'
                        }`}
                      >
                        {approved ? 'Approved' : 'Pending'}
                      </span>
                    </td>
                    {canManage && (
                      <td className="py-3 pr-5 whitespace-nowrap">
                        <button
                          type="button"
                          disabled={isSelf || isBusy}
                          onClick={() => handleDelete(auth_id, `${firstname} ${lastname}`)}
                          className="p-1.5 rounded-md text-danger hover:bg-danger/10 disabled:opacity-50"
                          aria-label="Remove user"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default UsersPage;
